import React, { useState } from 'react';
import { relazioniAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RelazioneAttachments } from './RelazioneAttachments';
import { FileDown, Pencil, Trash2, Loader2, MapPin, CalendarDays, User, Building2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/apiError';

const fmtDate = (v) => v ? new Date(v).toLocaleDateString('it-IT') : null;

const Section = ({ title, text, testid }) => (
    <div data-testid={testid}>
        <h4 className="font-heading uppercase text-sm text-primary tracking-wide mb-1">{title}</h4>
        {text?.trim() ? <p className="text-sm whitespace-pre-wrap leading-relaxed">{text}</p> : <p className="text-sm text-muted-foreground italic">—</p>}
    </div>
);

export const RelazioneDetailDialog = ({ relazione, open, onOpenChange, canEdit, onEdit, onDeleted }) => {
    const [pdfBusy, setPdfBusy] = useState(false);
    const [deleting, setDeleting] = useState(false);
    if (!relazione) return null;
    const r = relazione;

    const handlePdf = async () => {
        setPdfBusy(true);
        try {
            const res = await relazioniAPI.pdf(r.id);
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = `relazione_${(r.title || 'relazione').replace(/[^a-z0-9àèéìòù _-]/gi, '_').slice(0, 60)}.pdf`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nella generazione del PDF'));
        } finally { setPdfBusy(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Eliminare la relazione "${r.title}"? Verranno rimossi anche gli allegati.`)) return;
        setDeleting(true);
        try {
            await relazioniAPI.delete(r.id);
            toast.success('Relazione eliminata');
            onDeleted?.(r.id);
            onOpenChange(false);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nell\'eliminazione'));
        } finally { setDeleting(false); }
    };

    const periodo = (r.date_from || r.date_to) ? `${fmtDate(r.date_from) || '…'} → ${fmtDate(r.date_to) || '…'}` : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="relazione-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading uppercase pr-8" data-testid="relazione-detail-title">{r.title}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><MapPin size={14} /> <b className="text-foreground">{r.country}</b></span>
                    {periodo && <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {periodo}</span>}
                    <span className="inline-flex items-center gap-1"><User size={14} /> {r.author_name || '—'}</span>
                    <span className="text-xs">creata il {fmtDate(r.created_at)}</span>
                </div>
                {r.clients?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="relazione-detail-clients">
                        <Building2 size={14} className="text-muted-foreground" />
                        {r.clients.map(c => <Badge key={c.id} variant="secondary">{c.company_name}{c.city ? ` · ${c.city}` : ''}</Badge>)}
                    </div>
                )}
                {r.opportunity && (
                    <div className="text-sm inline-flex items-center gap-2" data-testid="relazione-detail-opportunity">
                        <Link2 size={14} className="text-muted-foreground" />
                        <span>{r.opportunity.kind === 'preventivo' ? 'Preventivo' : 'Commessa'}: <b>{r.opportunity.title}</b>{r.opportunity.commessa ? ` · ${r.opportunity.commessa}` : ''}{r.opportunity.telaio ? ` · ${r.opportunity.telaio}` : ''}</span>
                    </div>
                )}
                <div className="space-y-5 border-t pt-4">
                    <Section title="Analisi commerciale" text={r.analisi_commerciale} testid="relazione-detail-commerciale" />
                    <Section title="Analisi tecnica" text={r.analisi_tecnica} testid="relazione-detail-tecnica" />
                    <Section title="Analisi amministrativa" text={r.analisi_amministrativa} testid="relazione-detail-amministrativa" />
                </div>
                <div className="border-t pt-4">
                    <h4 className="font-heading uppercase text-sm tracking-wide mb-1">Allegati</h4>
                    <RelazioneAttachments relazioneId={r.id} canEdit={canEdit} />
                </div>
                <div className="flex flex-wrap justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={handlePdf} disabled={pdfBusy} data-testid="relazione-pdf">
                        {pdfBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FileDown size={14} className="mr-2" />} PDF
                    </Button>
                    {canEdit && (
                        <>
                            <Button variant="outline" onClick={() => onEdit?.(r)} data-testid="relazione-edit"><Pencil size={14} className="mr-2" /> Modifica</Button>
                            <Button variant="outline" className="text-destructive" onClick={handleDelete} disabled={deleting} data-testid="relazione-delete">
                                {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />} Elimina
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RelazioneDetailDialog;
